import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useApiService } from '../../services/api';
import { Lock, Save, User as UserIcon } from 'lucide-react';

const cardStyle = {
  background: '#fff',
  borderRadius: '16px',
  padding: '1.5rem',
  boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)',
  border: '1px solid #e5e7eb'
};

const inputStyle = {
  width: '100%',
  padding: '0.75rem 0.875rem',
  borderRadius: '10px',
  border: '1px solid #e5e7eb',
  background: '#f9fafb',
  fontSize: '0.95rem',
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease'
};

const labelStyle = {
  display: 'block',
  marginBottom: '0.4rem',
  fontWeight: 600,
  color: '#111827'
};

const buttonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.8rem 1.4rem',
  borderRadius: '10px',
  border: 'none',
  background: '#6366f1',
  color: '#fff',
  fontWeight: 600,
  cursor: 'pointer',
  boxShadow: '0 10px 15px -3px rgba(99,102,241,0.35)',
  transition: 'transform 0.15s ease, box-shadow 0.15s ease'
};

const helpTextStyle = {
  color: '#6b7280',
  fontSize: '0.8rem',
  marginTop: '0.25rem'
};

const sectionHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  marginBottom: '0.75rem',
  color: '#111827'
};

const AccountSettings = () => {
  const { user, updateStoredUser } = useAuth();
  const { updateUser } = useApiService();

  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const userId = useMemo(() => user?._id || user?.id, [user]);

  useEffect(() => {
    setName(user?.name || '');
    setEmail(user?.email || '');
  }, [user]);

  const getPasswordValidationError = (pwd) => {
    if (!pwd) return null; // optional
    const rules = [];
    if (pwd.length < 8) rules.push('at least 8 characters');
    if (!/[A-Z]/.test(pwd)) rules.push('one uppercase letter');
    if (!/[a-z]/.test(pwd)) rules.push('one lowercase letter');
    if (!/[0-9]/.test(pwd)) rules.push('one number');
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) rules.push('one special character');
    return rules.length ? `Password must include ${rules.join(', ')}.` : null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setStatus('');

    if (!userId) {
      setError('Missing user id. Please re-login and try again.');
      return;
    }

    if (!name.trim()) {
      setError('Name is required.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    const passwordError = getPasswordValidationError(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    const payload = { name: name.trim() };
    if (password) {
      payload.password = password;
    }

    try {
      setLoading(true);
      const updated = await updateUser(userId, payload);
      const nextUser = updated?.user || updated || { ...user, name: payload.name };
      updateStoredUser(nextUser);
      setStatus('Profile updated successfully.');
      setPassword('');
      setConfirmPassword('');
    } catch (err) {
      const message = err?.message || 'Failed to update profile.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '1.5rem', maxWidth: 720, margin: '0 auto' }}>
      <h2 style={{ fontSize: '1.75rem', color: '#111827', marginBottom: '1rem', fontWeight: 700 }}>
        Account Settings
      </h2>
      <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
        Update your profile information and change your password.
      </p>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div style={cardStyle}>
          <div style={sectionHeaderStyle}>
            <UserIcon size={20} />
            <div>
              <div style={{ fontWeight: 700 }}>Profile</div>
              <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>Update your name and view your email.</div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1rem' }}>
            <div>
              <label style={labelStyle}>Full Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={inputStyle}
                placeholder="Enter your name"
                required
              />
            </div>
            <div>
              <label style={labelStyle}>Email</label>
              <input
                type="email"
                value={email}
                style={{ ...inputStyle, background: '#eef2ff', cursor: 'not-allowed' }}
                disabled
              />
              <div style={helpTextStyle}>Email is managed by your administrator.</div>
            </div>
          </div>
        </div>

        <div style={cardStyle}>
          <div style={sectionHeaderStyle}>
            <Lock size={20} />
            <div>
              <div style={{ fontWeight: 700 }}>Password</div>
              <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>Leave blank to keep your current password.</div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1rem' }}>
            <div>
              <label style={labelStyle}>New Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={inputStyle}
                placeholder="Enter new password"
              />
              <div style={helpTextStyle}>
                Minimum 8 characters with uppercase, lowercase, number, and special character.
              </div>
            </div>
            <div>
              <label style={labelStyle}>Confirm New Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                style={inputStyle}
                placeholder="Confirm new password"
              />
            </div>
          </div>
        </div>

        {error && (
          <div style={{ padding: '0.85rem 1rem', borderRadius: '10px', background: '#fef2f2', color: '#991b1b', border: '1px solid #fecdd3' }}>
            {error}
          </div>
        )}
        {status && (
          <div style={{ padding: '0.85rem 1rem', borderRadius: '10px', background: '#ecfeff', color: '#0f766e', border: '1px solid #99f6e4' }}>
            {status}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="submit" style={buttonStyle} disabled={loading}>
            <Save size={18} />
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AccountSettings;






